/**
 * Drum challenge endpoints.
 *
 * Requests go through the app's shared axios instance because authentication
 * rides on an httpOnly cookie, and that client also carries the token-refresh
 * interceptor. A bare `fetch` would be rejected once the access token expires.
 */

import api from '../../services/authAPI';

/**
 * A leaderboard row. Keys are snake_case because they come straight back from
 * MySQL, matching how the rest of the app returns score rows.
 */
export interface DrumScoreEntry {
  id: number;
  username: string | null;
  score: number;
  accuracy: number;
  max_combo: number;
  perfect_count: number;
  good_count: number;
  miss_count: number;
  total_notes: number;
  grade: string;
  created_at: string;
}

/** The payload sent when a run finishes. */
export interface DrumScoreSubmission {
  instrumentSlug: string;
  score: number;
  accuracy: number;
  maxCombo: number;
  perfectCount: number;
  goodCount: number;
  missCount: number;
  totalNotes: number;
  grade: string;
}

/** One challenge's best score for the signed-in player. */
export interface DrumProgressEntry {
  challenge_id: string;
  best_score: number;
  best_grade: string;
  play_count: number;
}

/** Save a finished run. Requires a signed-in user. */
export async function submitDrumScore(
  challengeId: string,
  submission: DrumScoreSubmission,
): Promise<{ id: number }> {
  const { data } = await api.post<{ id: number }>(
    `/drums/challenges/${challengeId}/score`,
    submission,
  );
  return data;
}

/** Top scores for a challenge. Public — no sign-in needed. */
export async function fetchDrumLeaderboard(
  challengeId: string,
): Promise<DrumScoreEntry[]> {
  const { data } = await api.get<DrumScoreEntry[]>(
    `/drums/challenges/${challengeId}/scores`,
  );
  return data;
}

/** The signed-in player's best score on every challenge they have attempted. */
export async function fetchDrumProgress(): Promise<DrumProgressEntry[]> {
  const { data } = await api.get<DrumProgressEntry[]>('/drums/me/progress');
  return data;
}
